import { Component, OnDestroy, OnInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { skip, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { HttpClient } from '@angular/common/http';
import { parsePhoneNumber } from 'libphonenumber-js';

import { HeaderComponent } from '../../common/header/header.component';
import { SidebarComponent } from '../../common/slidebar/sidebar.components';
import { PaginatorComponent } from '../../common/paginator/paginator.component';

import { OsintSearchService } from '../../../services/osint-search/osint-search.service';
import { UserAccountService } from '../../../services/user-account/user-account.service';
import { SearchHistoryService } from '../../../services/search-history/search-history.service';
import { SearchPollingService } from '../../../services/search-polling/search-polling.service';
import { UtilsService } from '../../../services/utils/utils.service';
import { DocumentExportService } from '../../../services/document-export/document-export.service';
import { CountriesService } from '../../../services/countries/countries.service';
import { RestrictedAccessWarningDirective } from '../../../directives/restricted-access-warning.directive';
import { SearchServiceStatusService } from '../../../services/search-service-status/search-service-status.service';
import { CreditsLabelPipe } from '../../../pipes/credits-label.pipe';

@Component({
  selector: 'app-osint-number-search',
  standalone: true,
  imports: [CommonModule,
    FormsModule,
    HeaderComponent,
    PaginatorComponent,
    SidebarComponent,
    RestrictedAccessWarningDirective, CreditsLabelPipe],
  templateUrl: './osint-number-search.component.html',
  styleUrls: ['./osint-number-search.component.css'],
})
export class OsintNumberSearchComponent implements OnInit, OnDestroy {
  accessDetails: any;
  activeTab: string = 'number';
  availableCredits: number | null = null;
  creditsLoading = true;
  totalCredits: number = 0;

  singleNumber: string = '';

  textAreaInput: string = '';
  multipleNumbers: string[] = [];
  extraNumbers: Array<{
    value: string;
    countryCode: string;
    country: any;
  }> = [];
  readonly maxNumberRows = 5;
  countryDropdownRow: 'primary' | number | null = null;

  selectedCountryCode: string = '+91';
  isMultipleSearch: boolean = false;
  countries: any[] = [];
  selectedCountry: any = null;
  showCountryDropdown: boolean = false;
  countrySearchTerm: string = '';
  filteredCountries: any[] = [];

  allJobResultsSubscription?: Subscription;
  loadingJobIds: boolean = false;
  jobIds: string[] = [];

  private lastSearchTime: number = 0;
  private readonly SEARCH_THROTTLE_MS: number = 1000; // 1 second

  searchType: string = 'phone';
  loadingRecentResults: boolean = false;
  recentResults: any = [];
  recentResultsPaged: any = [];

  ipAddress: string = '';
  location: string = '';

  recentResultsCount: number = 0;
  recentResultsPerPage = 5;
  recentResultsCurrentPage = 1;
  recentResultsHasPreviousPage: boolean = false;
  recentResultsHasNextPage: boolean = false;

  recentResultsSelectAll = { isSelected: false };

  selectedFilterType: string = 'number';
  isServiceDisabled: boolean = false;
  searchCreditCost: number | null = null;

  filterTypes: any = [
    { label: 'Number', value: 'number' },
    // { label: 'Status', value: 'status' },
    { label: 'Notes', value: 'notes' },
    { label: 'Date Added', value: 'date-added' },
  ];

  filterKey: string = '';
  filterValue: string = '';

  numberFilter: string = '';
  statusFilter: string = '';
  notesFilter: string = '';
  startDateFilter: string = '';
  endDateFilter: string = '';

  get recentResultsPagedStartAndEnd() {
    if (!this.recentResults?.length || !this.recentResultsCount) {
      return { start: 0, end: 0 };
    }

    const start =
      (this.recentResultsCurrentPage - 1) * this.recentResultsPerPage + 1;
    const end = Math.min(
      start + this.recentResults.length - 1,
      this.recentResultsCount,
    );

    return { start, end };
  }

  get selectedRecentResults() {
    return this.recentResultsPaged
      .filter((recentResultPaged: any) => recentResultPaged.isSelected)
      .map((recentResultPaged: any) => recentResultPaged.osint_search_id);
  }

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private userAccountService: UserAccountService,
    private osintSearchService: OsintSearchService,
    private searchHistoryService: SearchHistoryService,
    private searchPollingService: SearchPollingService,
    private http: HttpClient,
    private utilsService: UtilsService,
    private documentExportService: DocumentExportService,
    public countriesService: CountriesService,
    private searchServiceStatusService: SearchServiceStatusService,
  ) {}

  ngOnInit() {
    this.accessDetails = this.retrieveAccessDetails();

    // Check if OSINT service is disabled
    this.searchServiceStatusService.searchServices$.subscribe((searchServices) => {
      if (searchServices) {
        this.isServiceDisabled = !searchServices.osint;
      }
    });

    this.searchServiceStatusService.creditCosts$.subscribe((cc) => {
      this.searchCreditCost = this.searchServiceStatusService.resolveCreditCost(
        cc,
        'osintSearchCost',
      );
    });

    this.countries = this.countriesService.getAllCountries();
    this.filteredCountries = this.countries;
    this.selectedCountry =
      this.countries.find((c) => c.dialCode === this.selectedCountryCode) ||
      this.countries[0];

    this.getRecentResults();
    this.getAvailableCredits();

    this.subscribeToJobResultsUpdates();

    // Check for auto-search from navigation
    const autoSearchState = history.state;
    if (autoSearchState?.autoSearch && autoSearchState?.searchQuery) {
      this.singleNumber = autoSearchState.searchQuery;
      // Clear the auto-search state to prevent re-triggering on back navigation
      history.replaceState(
        { ...history.state, autoSearch: false, searchQuery: null },
        '',
      );
      // Use setTimeout to ensure component is fully initialized
      setTimeout(() => {
        this.onSearchSingleNumberButtonClick();
      }, 100);
    }
  }

  get canAddNumberRow(): boolean {
    return this.extraNumbers.length < this.maxNumberRows - 1;
  }

  addNumberRow(): void {
    if (!this.canAddNumberRow) {
      this.toastr.error('You can only search a maximum of 5 numbers at once.');
      return;
    }
    this.extraNumbers = [
      ...this.extraNumbers,
      {
        value: '',
        countryCode: this.selectedCountryCode,
        country: this.selectedCountry,
      },
    ];
    this.isMultipleSearch = this.extraNumbers.length > 0;
  }

  removeNumberRow(index: number): void {
    this.extraNumbers = this.extraNumbers.filter((_, i) => i !== index);
    this.isMultipleSearch = this.extraNumbers.length > 0;
    if (this.countryDropdownRow === index) {
      this.showCountryDropdown = false;
      this.countryDropdownRow = null;
    }
  }

  toggleCountryDropdown(row: 'primary' | number = 'primary') {
    if (this.showCountryDropdown && this.countryDropdownRow === row) {
      this.showCountryDropdown = false;
      this.countryDropdownRow = null;
      return;
    }
    this.countryDropdownRow = row;
    this.showCountryDropdown = true;
    this.countrySearchTerm = '';
    this.filterCountries();
  }

  selectCountry(country: any) {
    if (this.countryDropdownRow === 'primary' || this.countryDropdownRow == null) {
      this.selectedCountry = country;
      this.selectedCountryCode = country.dialCode;
    } else {
      const row = this.extraNumbers[this.countryDropdownRow];
      if (row) {
        row.country = country;
        row.countryCode = country.dialCode;
      }
    }
    this.showCountryDropdown = false;
    this.countryDropdownRow = null;
    this.countrySearchTerm = '';
    this.filterCountries();
  }

  filterCountries() {
    if (!this.countrySearchTerm.trim()) {
      this.filteredCountries = this.countries;
    } else {
      const searchTerm = this.countrySearchTerm.toLowerCase().trim();
      const searchTermOriginal = this.countrySearchTerm.trim();
      const matchingCountries = this.countries.filter(
        (country) =>
          country.name.toLowerCase().includes(searchTerm) ||
          country.dialCode.includes(searchTermOriginal) ||
          country.code.toLowerCase().includes(searchTerm),
      );

      // Sort countries to show exact matches first, then partial matches
      this.filteredCountries = matchingCountries.sort((a, b) => {
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();
        const aDialCode = a.dialCode;
        const bDialCode = b.dialCode;
        const aCode = a.code.toLowerCase();
        const bCode = b.code.toLowerCase();

        // Exact match on dial code gets highest priority
        const aExactDialCode = aDialCode === searchTermOriginal;
        const bExactDialCode = bDialCode === searchTermOriginal;
        if (aExactDialCode && !bExactDialCode) return -1;
        if (!aExactDialCode && bExactDialCode) return 1;

        // Exact match on country code gets second priority
        const aExactCode = aCode === searchTerm;
        const bExactCode = bCode === searchTerm;
        if (aExactCode && !bExactCode) return -1;
        if (!aExactCode && bExactCode) return 1;

        // Starts with gets third priority
        const aStartsWith =
          aName.startsWith(searchTerm) ||
          aDialCode.startsWith(searchTermOriginal);
        const bStartsWith =
          bName.startsWith(searchTerm) ||
          bDialCode.startsWith(searchTermOriginal);
        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;

        // Otherwise, sort alphabetically by name
        return aName.localeCompare(bName);
      });
    }
  }

  onCountrySearchChange() {
    this.filterCountries();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    if (
      !target.closest('.country-select-wrapper') &&
      !target.closest('.country-dropdown')
    ) {
      this.showCountryDropdown = false;
      this.countryDropdownRow = null;
      this.countrySearchTerm = '';
      this.filterCountries();
    }
  }

  subscribeToJobResultsUpdates() {
    this.allJobResultsSubscription?.unsubscribe();

    this.allJobResultsSubscription = this.searchPollingService
      .getAllJobResults()
      .pipe(skip(1))
      .subscribe((map) => {
        this.recentResultsPaged.forEach((result: any) => {
          if (result.job_id) {
            const updatedStatus = map.get(result.job_id);
            if (updatedStatus) {
              result.done = updatedStatus.done || false;
              result.status = updatedStatus.status || 'pending';
            }
          }
        });
      });
  }

  ngOnDestroy() {
    this.allJobResultsSubscription?.unsubscribe();
  }

  retrieveAccessDetails() {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : null;

    return userPortal?.accessDetails || null;
  }

  setTab(tab: string) {
    this.activeTab = tab;
    switch (tab) {
      case 'image':
        this.router.navigate(['/osint-image-search']);
        break;
      case 'number':
        this.router.navigate(['/osint-number-search']);
        break;
      case 'email':
        this.router.navigate(['/osint-email-search']);
        break;
      case 'username':
        this.router.navigate(['/osint-username-search']);
        break;
      case 'name':
        this.router.navigate(['/osint-name-search']);
        break;
      case 'wallet':
        this.router.navigate(['/osint-wallet-search']);
        break;
    }
  }

  getAvailableCredits() {
    this.creditsLoading = true;
    this.userAccountService.getUserProfile().subscribe({
      next: (response) => {
        // console.log('GET USER PROFILE RESPONSE: ', response);

        const subscription = response?.data?.subscriptions?.[0] || {};
        this.availableCredits = this.userAccountService.pickAvailableCredits(response);
        this.creditsLoading = false;
        this.totalCredits = subscription?.total_credits;
      },
      error: (error) => {
        console.error(error);

        let errorMessage = 'Failed to load available credits';

        if (error?.error?.message_key) {
          errorMessage = error.error.message_key;
        } else if (error?.error?.message) {
          errorMessage = error.error.message;
        } else if (error?.message) {
          errorMessage = error.message;
        } else if (error?.statusText) {
          errorMessage = error.statusText;
        }

        this.toastr.error(errorMessage, 'Credits Load Failed');
        this.availableCredits = 0;
        this.creditsLoading = false;
      },
      complete: () => {},
    });
  }

  getRecentResults() {
    this.loadingRecentResults = true;

    this.searchHistoryService
      .getOsintHistories(
        this.searchType,
        this.recentResultsCurrentPage,
        this.recentResultsPerPage,
        this.filterKey,
        this.filterValue,
        this.startDateFilter,
        this.endDateFilter,
      )
      .subscribe({
        next: (response) => {
          // console.log('GET OSINT HISTORIES RESPONSE: ', response);
          this.loadingRecentResults = false;

          this.recentResults = response?.data?.data_list;
          this.recentResultsPaged = this.addAdditionalKeys(this.recentResults);
          // console.log('RECENT RESULTS PAGED: ', this.recentResultsPaged);

          this.recentResultsCount = response?.data?.total_items;
          this.recentResultsHasPreviousPage = response?.data?.has_previous;
          this.recentResultsHasNextPage = response?.data?.has_next;

          this.refreshPendingJobStatuses();
        },
        error: (error) => {
          console.error(error);
          this.loadingRecentResults = false;

          let errorMessage = 'Failed to load recent search results';

          if (error?.error?.message_key) {
            errorMessage = error.error.message_key;
          } else if (error?.error?.message) {
            errorMessage = error.error.message;
          } else if (error?.message) {
            errorMessage = error.message;
          } else if (error?.statusText) {
            errorMessage = error.statusText;
          }

          this.toastr.error(errorMessage, 'Load Results Failed');
        },
      });
  }

  refreshPendingJobStatuses() {
    this.recentResultsPaged.forEach((result: any) => {
      if (result.job_id) {
        const cachedStatus = this.searchPollingService.getCachedJobStatus(
          result.job_id,
        );
        if (cachedStatus) {
          result.done = cachedStatus.done || false;
          result.status = cachedStatus.status || 'pending';
        }
      }
    });

    const pendingJobIds = this.recentResultsPaged
      .filter(
        (result: any) =>
          result.job_id &&
          (!result.done ||
            result.done === false ||
            result.status === 'pending'),
      )
      .map((result: any) => result.job_id)
      .filter((jobId: string) => !!jobId);

    if (pendingJobIds.length > 0) {
      this.searchPollingService
        .checkJobStatus(pendingJobIds, (jobIds) =>
          this.osintSearchService.getSearchResults(this.searchType, jobIds),
        )
        .subscribe({
          next: (jobResultsMap) => {
            this.recentResultsPaged.forEach((result: any) => {
              if (result.job_id) {
                const updatedStatus = jobResultsMap.get(result.job_id);
                if (updatedStatus) {
                  result.done = updatedStatus.done || false;
                  result.status = updatedStatus.status || 'pending';
                }
              }
            });
          },
          error: (error) => {
            console.warn('Failed to refresh job statuses:', error);
          },
        });
    }
  }

  addAdditionalKeys(recentResults: any) {
    return recentResults.map((recentResult: any) => ({
      ...recentResult,
      isSelected: false,
    }));
  }

  onRecentResultPagedClick(event: Event, recentResultPaged: any) {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;

    this.updateRecentResultPaged(isChecked, recentResultPaged.osint_search_id);
  }

  updateRecentResultPaged(isChecked: boolean, searchId: string) {
    const recentResultsPagedToUpdate = this.recentResultsPaged.find(
      (recentResultPaged: any) =>
        recentResultPaged.osint_search_id === searchId,
    );

    if (recentResultsPagedToUpdate) {
      recentResultsPagedToUpdate.isSelected = isChecked;
    }
  }

  onRecentResultsPageChange(page: number) {
    this.recentResultsCurrentPage = page;
    this.recentResultsSelectAll.isSelected = false;

    this.getRecentResults();
  }

  onDeleteButtonClick() {
    this.deleteRecentResults();
  }

  deleteRecentResults() {
    this.searchHistoryService
      .deleteOsintHistories(this.selectedRecentResults)
      .subscribe({
        next: (response) => {
          // console.log('DELETE OSINT HISTORIES RESPONSE: ', response);

          this.recentResultsSelectAll.isSelected = false;

          this.updateRecentResultsPaged();
          this.updateRecentResultsCurrentPage();

          this.getRecentResults();
        },
        error: (error) => {
          console.error(error);

          let errorMessage = 'Failed to delete selected results';

          if (error?.error?.message_key) {
            errorMessage = error.error.message_key;
          } else if (error?.error?.message) {
            errorMessage = error.error.message;
          } else if (error?.message) {
            errorMessage = error.message;
          } else if (error?.statusText) {
            errorMessage = error.statusText;
          }

          this.toastr.error(errorMessage, 'Delete Failed');
        },
      });
  }

  updateRecentResultsPaged() {
    this.recentResultsPaged = this.recentResultsPaged.filter(
      (recentResultPaged: any) =>
        !this.selectedRecentResults.includes(recentResultPaged.osint_search_id),
    );
  }

  updateRecentResultsCurrentPage() {
    if (this.recentResultsPaged.length === 0) {
      if (this.recentResultsHasNextPage) {
      } else {
        if (this.recentResultsHasPreviousPage) {
          this.recentResultsCurrentPage--;
        }
      }
    }
  }

  onFilterTypeChange() {
    this.reset();
  }

  reset() {
    this.recentResultsCurrentPage = 1;

    this.filterKey = '';
    this.filterValue = '';

    this.numberFilter = '';
    this.statusFilter = '';
    this.notesFilter = '';
    this.startDateFilter = '';
    this.endDateFilter = '';
  }

  onMultipleSearchChange(): void {
    this.showCountryDropdown = false;
    this.countryDropdownRow = null;
    if (this.isMultipleSearch) {
      if (!this.textAreaInput.trim() && this.singleNumber.trim()) {
        this.textAreaInput = this.singleNumber.trim();
      }
      return;
    }
    if (!this.singleNumber.trim()) {
      const first = this.textAreaInput
        .split('\n')
        .map((value) => value.trim())
        .find((value) => value.length > 0);
      if (first) {
        this.singleNumber = first;
      }
    }
  }

  resetSearchInputs() {
    this.singleNumber = '';
    this.textAreaInput = '';
    this.multipleNumbers = [];
    this.extraNumbers = [];
    this.isMultipleSearch = false;
    this.showCountryDropdown = false;
    this.countryDropdownRow = null;
    this.countrySearchTerm = '';
  }

  onSearchSingleNumberButtonClick() {
    const entries = this.collectNumberEntries();
    if (!entries.length) {
      this.toastr.error('Please enter a phone number');
      return;
    }

    if (entries.length > this.maxNumberRows) {
      this.toastr.error('You can only search a maximum of 5 numbers at once.');
      return;
    }

    const invalid = entries.find(
      (entry) => !this.validateNumber(entry.value, entry.country, entry.countryCode),
    );
    if (invalid) {
      const countryName = invalid.country?.name || 'selected country';
      this.toastr.error(
        entries.length > 1
          ? `One or more phone numbers are invalid for ${countryName}`
          : `Phone number is invalid for ${countryName}`,
      );
      return;
    }

    if (this.isThrottled()) {
      return;
    }
    this.isMultipleSearch = entries.length > 1;
    this.multipleNumbers = entries.map((entry) => entry.value);
    this.getJobIds();
  }

  private collectNumberEntries(): Array<{
    value: string;
    country: any;
    countryCode: string;
  }> {
    const rawValues = this.isMultipleSearch
      ? this.textAreaInput.split('\n')
      : [this.singleNumber];
    return rawValues
      .map((value) => ({
        value: String(value || '').trim().replace(/\s+/g, ''),
        country: this.selectedCountry,
        countryCode: this.selectedCountryCode,
      }))
      .filter((row) => row.value.length > 0);
  }

  addCountryCode() {
    return this.collectNumberEntries().map((entry) => {
      if (entry.value.startsWith('+')) {
        return entry.value;
      }
      return `${entry.countryCode}${entry.value}`;
    });
  }

  private isThrottled(): boolean {
    const now = Date.now();
    const timeSinceLastSearch = now - this.lastSearchTime;

    return timeSinceLastSearch < this.SEARCH_THROTTLE_MS;
  }

  getJobIds() {
    const { ip, location } = this.retrieveIPAndLocation();

    //if (!ip) return;
    const payload = {
      type: 'phone',
      searchValues: this.addCountryCode(),
    };
    this.loadingJobIds = true;
    this.lastSearchTime = Date.now();

    this.osintSearchService
      .getJobIds(payload.type, payload.searchValues, ip, location)
      .subscribe({
        next: (response) => {
          // console.log('GET JOB IDS RESPONSE: ', response);

          this.jobIds = this.extractJobIds(response?.data);
          // console.log('JOB IDS: ', this.jobIds);

          this.searchPollingService.startPolling(this.jobIds, (jobIds) =>
            this.osintSearchService.getSearchResults('phone', jobIds),
          );
          this.searchPollingService.ensureBackgroundPolling(
            (this.jobIds || []).map((jobId: string, index: number) => ({
              job_id: jobId,
              searchType: 'phone',
              label: String(payload.searchValues?.[index] ?? jobId),
            })),
            3 * 1000,
          );
          this.getAllJobResults();

          this.reset();
          this.resetSearchInputs();
          this.getRecentResults();

          this.loadingJobIds = false;
        },
        error: (error) => {
          console.error(error);
          this.loadingJobIds = false;

          let errorMessage = 'An error occurred while processing your request';

          if (error?.error?.message_key) {
            errorMessage = error.error.message_key;
          } else if (error?.error?.message) {
            errorMessage = error.error.message;
          } else if (error?.message) {
            errorMessage = error.message;
          } else if (error?.statusText) {
            errorMessage = error.statusText;
          }

          this.toastr.error(errorMessage, 'Search Failed');
        },
      });
  }

  getAllJobResults() {}

  extractJobIds(jobs: any[]) {
    if (!jobs || !Array.isArray(jobs)) {
      return [];
    }

    if (jobs.length > 0 && typeof jobs[0] === 'string') {
      return jobs;
    }

    return jobs.filter((job) => job.done === false).map((job) => job.job_id);
  }

  validateNumber(
    phoneNumber: string,
    country: any = this.selectedCountry,
    dialCode: string = this.selectedCountryCode,
  ): boolean {
    if (!phoneNumber || !phoneNumber.trim()) {
      return false;
    }

    const trimmedNumber = phoneNumber.trim().replace(/\s+/g, '');

    // Get country code from selected country
    const countryCode = country?.code || this.selectedCountry?.code || null;

    if (!countryCode) {
      // Fallback to basic validation if country code is not available
      return /^\d+$/.test(trimmedNumber) && trimmedNumber.length >= 7;
    }

    try {
      // Check if number already has a country code
      let numberToValidate: string;

      if (trimmedNumber.startsWith('+')) {
        // Number already has country code
        numberToValidate = trimmedNumber;

        // Check if it has duplicate country codes (e.g., +91+919956895685)
        // This pattern occurs when country code appears twice consecutively
        const duplicatePattern = dialCode + dialCode;
        if (numberToValidate.includes(duplicatePattern)) {
          return false;
        }

        // Also check if the number starts with the selected country code followed by another +
        // This catches cases like +91+919956895685
        if (numberToValidate.startsWith(dialCode + '+')) {
          return false;
        }
      } else {
        // Add country code if not present
        numberToValidate = dialCode + trimmedNumber;
      }

      // Try to parse the phone number with the selected country
      const parsedNumber = parsePhoneNumber(
        numberToValidate,
        countryCode as any,
      );

      // Check if the number is valid for the selected country
      return parsedNumber.isValid();
    } catch (error) {
      // If parsing fails, return false
      return false;
    }
  }

  extractMultipleNumbers() {
    this.multipleNumbers = this.textAreaInput
      .split('\n')
      .map((number) => number.trim().replace(/\s+/g, ''))
      .filter((number) => number.length > 0);
  }

  validateNumbers() {
    return this.multipleNumbers.every((number) => this.validateNumber(number));
  }

  onSearchMultipleNumbersButtonClick() {
    this.onSearchSingleNumberButtonClick();
  }

  goToSearchResultPage(searchResult: any) {
    this.router.navigate(
      [`/osint-number-search/search-result/${searchResult.job_id}`],
      {
        state: { searchResult },
      },
    );
  }

  onRecentResultsSelectAllClick() {
    this.recentResultsSelectAll.isSelected =
      !this.recentResultsSelectAll.isSelected;

    this.recentResultsSelectAll.isSelected
      ? this.recentResultsPagedSelectAll()
      : this.recentResultsPagedDeselectAll();
  }

  recentResultsPagedSelectAll() {
    this.recentResultsPaged = this.recentResultsPaged.map(
      (recentResultPaged: any) => ({
        ...recentResultPaged,
        isSelected: true,
      }),
    );
  }

  recentResultsPagedDeselectAll() {
    this.recentResultsPaged = this.recentResultsPaged.map(
      (recentResultPaged: any) => ({
        ...recentResultPaged,
        isSelected: false,
      }),
    );
  }

  onPhoneFilterChange(phone: string) {
    const trimmedPhone = phone.trim();

    if (trimmedPhone === '') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    } else if (trimmedPhone.length === 1) {
      return;
    } else {
      this.filterKey = 'searched_phone';
      this.filterValue = trimmedPhone;

      this.getRecentResults();
    }
  }

  onStatusFilterChange(status: string) {
    this.statusFilter = status;

    if (this.statusFilter === 'all') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    }

    if (this.statusFilter === 'success') {
      this.filterKey = 'status';
      this.filterValue = 'success';

      this.getRecentResults();
    }

    if (this.statusFilter === 'pending') {
      this.filterKey = 'status';
      this.filterValue = 'pending';

      this.getRecentResults();
    }
  }

  onNotesFilterChange(notes: string) {
    const trimmedNotes = notes.trim();

    if (trimmedNotes === '') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    } else if (trimmedNotes.length === 1) {
      return;
    } else {
      this.filterKey = 'notes';
      this.filterValue = trimmedNotes;

      this.getRecentResults();
    }
  }

  onStartDateFilterChange(startDate: string) {
    this.startDateFilter = startDate;

    if (this.startDateFilter === '' && this.endDateFilter === '') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    }

    if (this.startDateFilter && this.endDateFilter) {
      this.filterKey = 'created_on';
      this.getRecentResults();
    }
  }

  onEndDateFilterChange(endDate: string) {
    this.endDateFilter = endDate;

    if (this.startDateFilter === '' && this.endDateFilter === '') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    }

    if (this.startDateFilter && this.endDateFilter) {
      this.filterKey = 'created_on';
      this.getRecentResults();
    }
  }

  onExportExcelButtonClick() {
    const transformedRecentResultsPaged = this.transformRecentResultsPaged();
    this.exportToExcel(transformedRecentResultsPaged);
  }

  transformRecentResultsPaged() {
    return this.recentResultsPaged.map((recentResultPaged: any) => ({
      'OSINT SEARCH ID': recentResultPaged.osint_search_id,
      'JOB ID': recentResultPaged.job_id,
      'SEARCHED PHONE': recentResultPaged.searched_phone,
      'USER EMAIL': recentResultPaged.user?.email || '',
      'IP ADDRESS': recentResultPaged.ip_address,
      'LOCATION': recentResultPaged.location,
      'DEVICE': recentResultPaged.device,
      'NOTES':
        recentResultPaged.notes === undefined ? null : recentResultPaged.notes,
      'CREATED ON': this.utilsService.formatDateTime(
        recentResultPaged.created_on,
        'MMM d, y, h:mm:ss a',
      ),
    }));
  }

  exportToExcel(transformedRecentResultsPaged: any[]) {
    if (!transformedRecentResultsPaged.length) {
      this.toastr.info('No recent results to export');
      return;
    }

    const timestamp = this.utilsService.formatDateTime(
      new Date().toISOString(),
      'dd-MM-yyyy_HH-mm-ss',
    );

    const fileName = `osint-number-results-${timestamp || 'export'}`;
    this.documentExportService.exportToExcel(
      transformedRecentResultsPaged,
      fileName,
      'OSINT Results',
    );
  }

  onExportCsvButtonClick() {
    const transformedRecentResultsPaged = this.transformRecentResultsPaged();
    this.exportToCsv(transformedRecentResultsPaged);
  }

  exportToCsv(transformedRecentResultsPaged: any[]) {
    if (!transformedRecentResultsPaged.length) {
      this.toastr.info('No recent results to export');
      return;
    }

    const timestamp = this.utilsService.formatDateTime(
      new Date().toISOString(),
      'dd-MM-yyyy_HH-mm-ss',
    );

    const fileName = `osint-number-results-${timestamp || 'export'}`;
    this.documentExportService.exportToCsv(
      transformedRecentResultsPaged,
      fileName,
      'OSINT Results',
    );
  }

  onExportPdfButtonClick() {
    const transformedRecentResultsPaged = this.transformRecentResultsPaged();
    this.exportToPdf(transformedRecentResultsPaged);
  }

  exportToPdf(transformedRecentResultsPaged: any[]) {
    if (!transformedRecentResultsPaged.length) {
      this.toastr.info('No recent results to export');
      return;
    }

    const timestamp = this.utilsService.formatDateTime(
      new Date().toISOString(),
      'dd-MM-yyyy_HH-mm-ss',
    );

    const fileName = `osint-number-results-${timestamp || 'export'}`;
    const title = `OSINT results generated from Ryno`;
    const username = this.getUsername();

    this.documentExportService.exportResultsToPdf(
      transformedRecentResultsPaged,
      fileName,
      title,
      username,
    );
  }

  private getUsername(): string {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : null;

    return userPortal?.loginDetails?.user_name || '';
  }

  private retrieveIPAndLocation(): { ip: string; location: string } {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : null;

    const ipRaw = userPortal?.ipDetails?.ip || '';
    const location = userPortal?.ipDetails?.city || '';

    const ip = ipRaw.trim();
    const isValidIPv4 = /^\d{1,3}(\.\d{1,3}){3}$/.test(ip);

    return {
      ip: isValidIPv4 ? ip : '',
      location,
    };
  }

  onBuyCreditsButtonClick() {
    this.router.navigate(['/plan-pricing']);
  }

  /** Truncate text to max words; if longer, append " ....." */
  truncateToWords(text: string | null | undefined, maxWords: number = 20): string {
    if (text == null || text === '') return '';
    const words = text.trim().split(/\s+/);
    if (words.length <= maxWords) return text;
    return words.slice(0, maxWords).join(' ') + ' .....';
  }
}
